import GuideLayout from "../components/GuideLayout";
import { useLang } from "../i18n/LanguageContext";
import { faqPageLd } from "../lib/schema";

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Private chauffeur / luxury driver service",
  name: "Private Driver in Tuscany — Luxury Chauffeur Service",
  provider: { "@id": "https://yourblacklimo.com/#business" },
  areaServed: ["Tuscany", "Lucca", "Pisa", "Florence", "Siena", "Livorno", "La Spezia", "Viareggio", "Carrara"],
  description:
    "Private driver and luxury chauffeur service across Tuscany in a Mercedes V-Class with an English-speaking driver: airport and cruise-port transfers, bespoke day trips and hourly hire.",
  url: "https://yourblacklimo.com/private-driver-tuscany",
};

const CONTENT = {
  en: {
    seoTitle: "Private Driver in Tuscany | Luxury Chauffeur Service — Your Black Limo",
    seoDesc:
      "Private driver and luxury chauffeur service in Tuscany. English-speaking driver, Mercedes V-Class, door-to-door: airport & cruise-port transfers, bespoke day trips and hourly hire from Lucca, Pisa and Florence.",
    overline: "Private Driver Tuscany",
    h1: "Private Driver in Tuscany — A Luxury Chauffeur Service",
    intro:
      "One professional, English-speaking chauffeur and one spotless Mercedes V-Class, entirely at your disposal across Tuscany. From airport and cruise-port transfers to bespoke day trips and hourly hire — understated luxury, door to door, at your own pace.",
    heroAlt: "Private chauffeur beside a black Mercedes V-Class on a Tuscan road",
    blocks: [
      {
        title: "Transfers, door to door",
        body: "Airports (Pisa and Florence), cruise ports (Livorno and La Spezia) and private-yacht marinas (Viareggio and Carrara) — met with a name board, flights and ships tracked, and driven directly to your hotel, villa or any address in Tuscany.",
      },
      {
        title: "Day trips & hourly hire",
        body: "Keep your chauffeur on hand for as long as you need. A full day among the vineyards, a city at your own rhythm, or a car simply on call between meetings — the V-Class becomes your private lounge between stops.",
      },
    ],
    cards: {
      overline: "How guests use us",
      title: "One driver, every kind of Tuscan day",
      items: [
        { title: "Airport transfers", body: "Seamless door-to-door transfers from Pisa and Florence airports, with flight tracking and a chauffeur waiting on arrival." },
        { title: "Cruise shore excursions", body: "Private days ashore from Livorno and La Spezia — Florence, Pisa, Lucca and the Cinque Terre, always back to the ship on time." },
        { title: "Bespoke day trips", body: "Chianti, Val d'Orcia, Siena and San Gimignano — tailored itineraries built entirely around you." },
        { title: "Hourly hire", body: "Weddings, meetings or a day of shopping — a private car and chauffeur on call for as long as you need." },
      ],
    },
    note: {
      title: "Why a private driver",
      body:
        "No timetables, no shared groups, no waiting. Just a fully licensed NCC service, a professional English-speaking chauffeur and a Mercedes V-Class for up to seven guests with luggage — child seats on request. We provide the transportation; entrance fees, meals, guided tours and other third-party services are booked and paid directly by the client.",
    },
    cta: {
      title: "Book your private driver in Tuscany",
      secondaryLabel: "See our services",
      secondaryHref: "/services",
      primaryLabel: "Request a quote",
      primaryHref: "/#contact",
    },
    faq: {
      overline: "Questions",
      title: "Frequently asked questions",
      items: [
        { q: "Do your drivers speak English?", a: "Yes. All our chauffeurs are professional, English-speaking drivers who know Tuscany intimately." },
        { q: "What areas do you cover?", a: "All of Tuscany and the nearby coast — Lucca, Pisa, Florence, Siena, Livorno, La Spezia, Viareggio, Carrara, the Cinque Terre and beyond." },
        { q: "Can I hire a private driver by the hour or for a full day?", a: "Yes. You can book point-to-point transfers, a full or half-day at disposal, or hourly hire — the chauffeur stays with you for as long as you need." },
        { q: "How many passengers fit in the vehicle?", a: "Up to seven guests with luggage in a Mercedes V-Class, with child seats available on request." },
      ],
    },
  },
  it: {
    seoTitle: "Autista Privato in Toscana | Servizio Chauffeur di Lusso — Your Black Limo",
    seoDesc:
      "Autista privato e servizio chauffeur di lusso in Toscana. Autista che parla inglese, Mercedes Classe V, porta a porta: transfer aeroportuali e crociere, gite su misura e noleggio a ore da Lucca, Pisa e Firenze.",
    overline: "Autista Privato Toscana",
    h1: "Autista Privato in Toscana — Un Servizio Chauffeur di Lusso",
    intro:
      "Un autista professionista che parla inglese e un'impeccabile Mercedes Classe V, interamente a tua disposizione in tutta la Toscana. Dai transfer aeroportuali e crociere alle gite su misura e al noleggio a ore — lusso discreto, porta a porta, al tuo ritmo.",
    heroAlt: "Autista privato accanto a una Mercedes Classe V nera su una strada toscana",
    blocks: [
      {
        title: "Transfer, porta a porta",
        body: "Aeroporti (Pisa e Firenze), porti crociere (Livorno e La Spezia) e marina per yacht (Viareggio e Carrara) — accoglienza con cartello, monitoraggio di voli e navi, e trasporto diretto al tuo hotel, villa o qualsiasi indirizzo in Toscana.",
      },
      {
        title: "Gite e noleggio a ore",
        body: "Tieni il tuo autista a disposizione per tutto il tempo che ti serve. Una giornata intera tra i vigneti, una città al tuo ritmo o un'auto semplicemente a chiamata tra un impegno e l'altro — la Classe V diventa il tuo salotto privato tra una tappa e l'altra.",
      },
    ],
    cards: {
      overline: "Come ci usano i clienti",
      title: "Un autista, ogni tipo di giornata toscana",
      items: [
        { title: "Transfer aeroportuali", body: "Transfer porta a porta senza pensieri dagli aeroporti di Pisa e Firenze, con monitoraggio voli e autista ad attenderti all'arrivo." },
        { title: "Escursioni da crociera", body: "Giornate private a terra da Livorno e La Spezia — Firenze, Pisa, Lucca e le Cinque Terre, sempre a bordo in orario." },
        { title: "Gite su misura", body: "Chianti, Val d'Orcia, Siena e San Gimignano — itinerari costruiti interamente su di te." },
        { title: "Noleggio a ore", body: "Matrimoni, riunioni o una giornata di shopping — auto privata con autista a chiamata per tutto il tempo che ti serve." },
      ],
    },
    note: {
      title: "Perché un autista privato",
      body:
        "Niente orari, niente gruppi condivisi, niente attese. Solo un servizio NCC regolarmente autorizzato, un autista professionista che parla inglese e una Mercedes Classe V fino a sette ospiti con bagagli — seggiolini su richiesta. Forniamo il trasporto; biglietti d'ingresso, pasti, visite guidate e altri servizi di terzi sono prenotati e pagati direttamente dal cliente.",
    },
    cta: {
      title: "Prenota il tuo autista privato in Toscana",
      secondaryLabel: "Vedi i servizi",
      secondaryHref: "/services",
      primaryLabel: "Richiedi un preventivo",
      primaryHref: "/#contact",
    },
    faq: {
      overline: "Domande",
      title: "Domande frequenti",
      items: [
        { q: "I vostri autisti parlano inglese?", a: "Sì. Tutti i nostri autisti sono professionisti che parlano inglese e conoscono la Toscana in modo approfondito." },
        { q: "Quali zone coprite?", a: "Tutta la Toscana e la costa vicina — Lucca, Pisa, Firenze, Siena, Livorno, La Spezia, Viareggio, Carrara, le Cinque Terre e oltre." },
        { q: "Posso avere un autista privato a ore o per l'intera giornata?", a: "Sì. Puoi prenotare transfer punto a punto, mezza o intera giornata a disposizione oppure noleggio a ore — l'autista resta con te per tutto il tempo che ti serve." },
        { q: "Quanti passeggeri entrano nel veicolo?", a: "Fino a sette ospiti con bagagli in una Mercedes Classe V, con seggiolini per bambini su richiesta." },
      ],
    },
  },
};

export default function PrivateDriverTuscany() {
  const { lang } = useLang();
  const c = CONTENT[lang === "it" ? "it" : "en"];
  const { ["@context"]: _ctx, ...serviceNode } = jsonLd;
  const graphLd = { "@context": "https://schema.org", "@graph": [serviceNode, faqPageLd(c.faq.items)] };
  return (
    <GuideLayout
      testid="private-driver-tuscany-guide"
      faq={c.faq}
      seo={{ title: c.seoTitle, description: c.seoDesc, path: "/private-driver-tuscany", locale: lang === "it" ? "it_IT" : "en_US", jsonLd: graphLd }}
      overline={c.overline}
      h1={c.h1}
      intro={c.intro}
      heroImage={"https://static.prod-images.emergentagent.com/jobs/c98acbe0-6e23-474e-adcd-5ab166743bcb/images/3f77adce02b6d1d0a6f3e641713c864714ca2916bdce778a2057cf6086361f0d.jpeg"}
      heroAlt={c.heroAlt}
      blocks={c.blocks}
      cards={c.cards}
      note={c.note}
      cta={c.cta}
    />
  );
}
